import { useEffect } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";

import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import Button from "ds/components/Button";
import Box from "ds/components/Box";
import { MutationAccountUpdateDefaultWorkerPoolRunnerImagesArgs } from "types/generated";

import { useUpdateDefaultRunnerImages } from "./useUpdateDefaultRunnerImages";
import { getFormDefaultValues } from "./helpers";

type RuntimeSecurityRunnerImageFormProps = {
  defaultPublicWorkerPoolRunnerImage?: string;
  defaultPrivateWorkerPoolRunnerImage?: string;
};

type RuntimeSecurityRunnerImageFormFields = {
  publicWorkerPoolRunnerImage: string;
  privateWorkerPoolRunnerImage: string;
};

const RuntimeSecurityRunnerImageForm = (props: RuntimeSecurityRunnerImageFormProps) => {
  const { defaultPublicWorkerPoolRunnerImage, defaultPrivateWorkerPoolRunnerImage } = props;

  const form = useForm<RuntimeSecurityRunnerImageFormFields>({
    defaultValues: getFormDefaultValues(
      defaultPublicWorkerPoolRunnerImage,
      defaultPrivateWorkerPoolRunnerImage
    ),
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
  } = form;

  useEffect(() => {
    reset(
      getFormDefaultValues(defaultPublicWorkerPoolRunnerImage, defaultPrivateWorkerPoolRunnerImage)
    );
  }, [reset, defaultPublicWorkerPoolRunnerImage, defaultPrivateWorkerPoolRunnerImage]);

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationRuntimeSecurity,
  });

  const { onUpdate, loading } = useUpdateDefaultRunnerImages(["GetAccountDefaultRunnerImages"]);

  const handleSubmitClick = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit: SubmitHandler<RuntimeSecurityRunnerImageFormFields> = (
    formFields: RuntimeSecurityRunnerImageFormFields
  ) => {
    trackSegmentAnalyticsEvent("Save");

    const input: MutationAccountUpdateDefaultWorkerPoolRunnerImagesArgs = {
      publicWorkerPoolRunnerImage: formFields.publicWorkerPoolRunnerImage,
      privateWorkerPoolRunnerImage: formFields.privateWorkerPoolRunnerImage,
    };

    onUpdate(input);
  };

  return (
    <FormProvider {...form}>
      <FormField
        label="Public runner image"
        isOptional
        error={errors?.publicWorkerPoolRunnerImage?.message}
        helperText="Default image for public workers. Leave blank to use Spacelift's default runner image."
        tooltipInfo={
          <>
            <TooltipModalTitle>Public runner image</TooltipModalTitle>
            <TooltipModalBody align="start">
              The runner image that will be used when there's no runner image set for the run
              through the stack configuration or through the runtime configuration.
            </TooltipModalBody>
          </>
        }
        tooltipInfoVariant="modal"
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="public.ecr.aws/spacelift/runner-terraform:latest"
            {...register("publicWorkerPoolRunnerImage")}
            error={!!errors?.publicWorkerPoolRunnerImage?.message}
            {...ariaInputProps}
          />
        )}
      </FormField>
      <FormField
        label="Private runner image"
        isOptional
        error={errors?.privateWorkerPoolRunnerImage?.message}
        helperText="Default image for private workers. Leave blank to use Spacelift's default runner image."
        tooltipInfo={
          <>
            <TooltipModalTitle>Private runner image</TooltipModalTitle>
            <TooltipModalBody align="start">
              The runner image that will be used when there's no runner image set for the run
              through the stack configuration or through the runtime configuration.
            </TooltipModalBody>
          </>
        }
        tooltipInfoVariant="modal"
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="public.ecr.aws/spacelift/runner-terraform:latest"
            {...register("privateWorkerPoolRunnerImage")}
            error={!!errors?.privateWorkerPoolRunnerImage?.message}
            {...ariaInputProps}
          />
        )}
      </FormField>
      <Box justify="end" gap="large" margin="x-large 0 0">
        {isDirty && (
          <Button variant="secondary" onPress={() => reset()}>
            Cancel
          </Button>
        )}
        <Button variant="primary" onPress={handleSubmitClick} disabled={loading}>
          Save
        </Button>
      </Box>
    </FormProvider>
  );
};

export default RuntimeSecurityRunnerImageForm;
